<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxQualityIndex from './InboxQualityIndex.vue';

const props = defineProps({
  healthData: {
    type: Object,
    default: null,
  },
  isRegisteringWebhook: {
    type: Boolean,
    default: false,
  },
  isUpdatingDirectSend: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['registerWebhook', 'updateDirectSend']);

const { t } = useI18n();

const QUALITY_COLORS = {
  GREEN: 'text-n-teal-11',
  YELLOW: 'text-n-amber-11',
  RED: 'text-n-ruby-11',
  UNKNOWN: 'text-n-slate-12',
};

const STATUS_COLORS = {
  APPROVED: 'text-n-teal-11',
  PENDING_REVIEW: 'text-n-amber-11',
  AVAILABLE_WITHOUT_REVIEW: 'text-n-teal-11',
  REJECTED: 'text-n-ruby-9',
  DECLINED: 'text-n-ruby-9',
};

const MODE_COLORS = {
  LIVE: 'text-n-teal-11',
  SANDBOX: 'text-n-slate-11',
};

const healthItems = computed(() => {
  if (!props.healthData) {
    return [];
  }

  const {
    display_phone_number: displayPhoneNumber,
    verified_name: verifiedName,
    name_status: nameStatus,
    quality_rating: qualityRating,
    messaging_limit_tier: messagingLimitTier,
    account_mode: accountMode,
  } = props.healthData;

  return [
    {
      key: 'displayPhoneNumber',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.LABEL'),
      value: displayPhoneNumber || 'N/A',
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.TOOLTIP'
      ),
      show: true,
    },
    {
      key: 'verifiedName',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.LABEL'),
      value: verifiedName || 'N/A',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.TOOLTIP'),
      show: true,
    },
    {
      key: 'displayNameStatus',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.LABEL'),
      value: nameStatus || 'UNKNOWN',
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.TOOLTIP'
      ),
      show: true,
      type: 'status',
    },
    {
      key: 'qualityRating',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.LABEL'),
      value: qualityRating || 'UNKNOWN',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.TOOLTIP'),
      show: true,
      type: 'quality',
    },
    {
      key: 'messagingLimitTier',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.LABEL'),
      value: messagingLimitTier || 'UNKNOWN',
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.TOOLTIP'
      ),
      show: true,
      type: 'tier',
    },
    {
      key: 'accountMode',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.LABEL'),
      value: accountMode || 'UNKNOWN',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.TOOLTIP'),
      show: true,
      type: 'mode',
    },
  ];
});

const handleGoToSettings = () => {
  const { business_id: businessId } = props.healthData || {};

  if (businessId) {
    // WhatsApp Business Manager URL with specific business ID and phone numbers tab
    const whatsappBusinessUrl = `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${businessId}&tab=phone-numbers`;
    window.open(whatsappBusinessUrl, '_blank');
  } else {
    // Fallback to general WhatsApp Business Manager if business_id is not available
    const fallbackUrl = 'https://business.facebook.com/';
    window.open(fallbackUrl, '_blank');
  }
};

const getQualityRatingTextColor = rating =>
  QUALITY_COLORS[rating] || QUALITY_COLORS.UNKNOWN;

const formatTierDisplay = tier => {
  switch (tier) {
    case 'TIER_250':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_250');
    case 'TIER_1000':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_1000');
    case 'TIER_1K':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_1K');
    case 'TIER_10K':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_10K');
    case 'TIER_100K':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_100K');
    case 'TIER_UNLIMITED':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.TIER_UNLIMITED');
    default:
      return tier;
  }
};

const formatStatusDisplay = status => {
  switch (status) {
    case 'APPROVED':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.APPROVED');
    case 'PENDING_REVIEW':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.PENDING_REVIEW');
    case 'AVAILABLE_WITHOUT_REVIEW':
      return t(
        'INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.AVAILABLE_WITHOUT_REVIEW'
      );
    case 'REJECTED':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.REJECTED');
    case 'DECLINED':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.DECLINED');
    case 'NON_EXISTS':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.NON_EXISTS');
    default:
      return status;
  }
};

const formatModeDisplay = mode => {
  switch (mode) {
    case 'SANDBOX':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.MODES.SANDBOX');
    case 'LIVE':
      return t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.MODES.LIVE');
    default:
      return mode;
  }
};

const getModeStatusTextColor = mode => MODE_COLORS[mode] || 'text-n-slate-12';

const getStatusTextColor = status => STATUS_COLORS[status] || 'text-n-slate-12';

const showWebhookSection = computed(
  () => props.healthData?.webhook_configuration !== undefined
);

// Phone-level override takes precedence over WABA-level (application), so prefer it.
const webhookUrl = computed(
  () =>
    props.healthData?.webhook_configuration?.phone_number ||
    props.healthData?.webhook_configuration?.whatsapp_business_account ||
    props.healthData?.webhook_configuration?.application
);

const webhookConfigured = computed(() => !!webhookUrl.value);

const normalizeWebhookUrl = url => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);
    // Meta may serialize the callback URL with a trailing slash while the
    // configured endpoint is stored without it. Those URLs are equivalent.
    parsedUrl.pathname = parsedUrl.pathname.replace(/\/+$/, '') || '/';
    return parsedUrl.toString().replace(/\/$/, '');
  } catch {
    return url.replace(/\/+$/, '');
  }
};

const webhookUrlMismatch = computed(
  () =>
    webhookConfigured.value &&
    normalizeWebhookUrl(webhookUrl.value) !==
      normalizeWebhookUrl(props.healthData?.expected_webhook_url)
);

const handleRegisterWebhook = () => {
  emit('registerWebhook');
};

const directSendStatus = computed(
  () => props.healthData?.direct_send_status || 'unknown'
);
const isDirectSendConfirmOpen = ref(false);
const pendingDirectSendStatus = ref(null);

const handleDirectSendChange = event => {
  const enabled = event.target.checked;
  event.target.checked = directSendStatus.value === 'available';
  pendingDirectSendStatus.value = enabled ? 'available' : 'disabled';
  isDirectSendConfirmOpen.value = true;
};

const cancelDirectSendChange = () => {
  pendingDirectSendStatus.value = null;
  isDirectSendConfirmOpen.value = false;
};

const confirmDirectSendChange = () => {
  if (!pendingDirectSendStatus.value) return;

  emit('updateDirectSend', pendingDirectSendStatus.value);
  cancelDirectSendChange();
};

const openDirectSendGuide = () => {
  const accountMatch = window.location.pathname.match(/\/app\/accounts\/(\d+)/);
  const accountId = accountMatch?.[1];
  if (accountId) {
    window.location.href = `/app/accounts/${accountId}/helpcenter?article=direct-send`;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="w-full gap-4">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.TITLE') }}
          </span>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.DESCRIPTION') }}
          </p>
        </div>
        <ButtonV4
          sm
          solid
          blue
          class="flex-shrink-0"
          @click="handleGoToSettings"
        >
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.GO_TO_SETTINGS') }}
        </ButtonV4>
      </div>

      <div v-if="healthData" class="grid grid-cols-1 gap-4 xs:grid-cols-2">
        <div
          v-for="item in healthItems"
          :key="item.key"
          class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-2 items-center">
            <span class="text-body-main font-medium text-n-slate-11">
              {{ item.label }}
            </span>
            <Icon
              v-tooltip.top="item.tooltip"
              icon="i-lucide-info"
              class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
            />
          </div>
          <div class="flex items-center">
            <span
              v-if="item.type === 'quality'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
              :class="getQualityRatingTextColor(item.value)"
            >
              {{ item.value }}
            </span>
            <span
              v-else-if="item.type === 'status'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
              :class="getStatusTextColor(item.value)"
            >
              {{ formatStatusDisplay(item.value) }}
            </span>
            <span
              v-else-if="item.type === 'mode'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
              :class="getModeStatusTextColor(item.value)"
            >
              {{ formatModeDisplay(item.value) }}
            </span>
            <span
              v-else-if="item.type === 'tier'"
              class="text-label text-n-slate-12"
            >
              {{ formatTierDisplay(item.value) }}
            </span>
            <span v-else class="text-label text-n-slate-12">{{
              item.value
            }}</span>
          </div>
        </div>

        <!-- Webhook configuration card -->
        <div
          v-if="showWebhookSection"
          class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-2 items-center">
            <span class="text-body-main font-medium text-n-slate-11">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.TITLE') }}
            </span>
            <Icon
              v-tooltip.top="t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.DESCRIPTION')"
              icon="i-lucide-info"
              class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
            />
          </div>
          <div class="flex items-center justify-between gap-3">
            <span
              v-if="webhookConfigured && !webhookUrlMismatch"
              class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-teal-11"
            >
              <Icon icon="i-lucide-check-circle" class="w-3.5 h-3.5" />
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.CONFIGURED_SUCCESS') }}
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-amber-11"
            >
              <Icon icon="i-lucide-alert-triangle" class="w-3.5 h-3.5" />
              {{
                webhookUrlMismatch
                  ? t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.URL_MISMATCH')
                  : t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.ACTION_REQUIRED')
              }}
            </span>
            <ButtonV4
              v-if="!webhookConfigured || webhookUrlMismatch"
              sm
              solid
              blue
              :loading="isRegisteringWebhook"
              :disabled="isRegisteringWebhook"
              class="flex-shrink-0"
              @click="handleRegisterWebhook"
            >
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REGISTER_BUTTON') }}
            </ButtonV4>
          </div>
        </div>

        <div
          v-if="healthData"
          class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-2 items-center">
            <span class="text-body-main font-medium text-n-slate-11">
              {{ $t('ACCOUNT_HEALTH.DIRECT_SEND') }}
            </span>
            <Icon
              v-tooltip.top="
                'Permite enviar sem modelo somente quando a Meta habilitou o recurso para esta caixa.'
              "
              icon="i-lucide-info"
              class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
            />
          </div>
          <div class="flex items-center justify-between gap-3">
            <div>
              <span
                class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2"
                :class="
                  directSendStatus === 'available'
                    ? 'text-n-teal-11'
                    : 'text-n-amber-11'
                "
              >
                {{
                  directSendStatus === 'available'
                    ? $t('ACCOUNT_HEALTH.DIRECT_SEND_ENABLED')
                    : directSendStatus === 'disabled'
                      ? $t('ACCOUNT_HEALTH.DIRECT_SEND_DISABLED')
                      : $t('ACCOUNT_HEALTH.DIRECT_SEND_UNVERIFIED')
                }}
              </span>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ $t('ACCOUNT_HEALTH.PERMISSION_FAILURE') }}
              </p>
              <button
                type="button"
                class="mt-2 text-xs font-medium text-n-brand hover:underline"
                @click="openDirectSendGuide"
              >
                {{ $t('ACCOUNT_HEALTH.LEARN_MORE') }}
              </button>
            </div>
            <label
              class="inline-flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                :checked="directSendStatus === 'available'"
                :disabled="isUpdatingDirectSend"
                @change="handleDirectSendChange"
              />
              {{ $t('ACCOUNT_HEALTH.ENABLE') }}
            </label>
          </div>
        </div>
      </div>

      <div v-else class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-activity" class="mb-2 w-8 h-8" />
            <p class="text-body-main text-n-slate-11">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quality Index Component -->
    <InboxQualityIndex :inbox-id="inboxId" />

    <div
      v-if="isDirectSendConfirmOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      @click.self="cancelDirectSendChange"
    >
      <div
        class="w-full max-w-md rounded-xl bg-n-solid-2 p-6 shadow-2xl outline outline-1 -outline-offset-1 outline-n-weak"
        role="dialog"
        aria-modal="true"
        aria-labelledby="direct-send-confirm-title"
      >
        <div class="flex items-start gap-3">
          <Icon
            icon="i-lucide-shield-alert"
            class="mt-0.5 h-5 w-5 flex-shrink-0 text-n-amber-11"
          />
          <div>
            <h2
              id="direct-send-confirm-title"
              class="text-heading-3 text-n-slate-12"
            >
              {{
                pendingDirectSendStatus === 'available'
                  ? $t('ACCOUNT_HEALTH.ENABLE_DIRECT_SEND')
                  : $t('ACCOUNT_HEALTH.DISABLE_DIRECT_SEND')
              }}
            </h2>
            <p class="mt-2 text-body-main text-n-slate-11">
              <template v-if="pendingDirectSendStatus === 'available'">
                {{ $t('ACCOUNT_HEALTH.ENABLE_CONFIRMATION') }}
              </template>
              <template v-else>
                {{ $t('ACCOUNT_HEALTH.DISABLE_CONFIRMATION') }}
              </template>
            </p>
          </div>
        </div>
        <div class="mt-6 flex justify-end gap-3">
          <ButtonV4 sm @click="cancelDirectSendChange">
            {{ $t('DIALOG.BUTTONS.CANCEL') }}
          </ButtonV4>
          <ButtonV4 sm solid blue @click="confirmDirectSendChange">
            {{ $t('DIALOG.BUTTONS.CONFIRM') }}
          </ButtonV4>
        </div>
      </div>
    </div>
  </div>
</template>
